import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { FREELANCER_HOME_PAGE, FREELANCER_REGISTER_PAGE } from "../../freelancer/routes";
import { COLABORADOR_HOME, COLABORADOR_PRIMEIRO_ACESSO } from "../../colaborador/routes";

import { changeFirstLoginPasswordApi, loginApi, LoginResponse } from "../authApi";
import { loginFreelancerApi } from "../../freelancer/freelancerApi";
import { loginEmployeeApi } from "../../colaborador/api/employeeAuthApi";

import { setCredentials } from "../../../store/authSlice";
import { setFreelancerSession } from "../../../store/freelancerAuthSlice";
import { setEmployeeSession } from "../../../store/employeeAuthSlice";

import { PASSWORD_RULES, isStrongPassword } from "../../colaborador/components/PasswordRules";
import { staffHomeFromToken } from "../../../utils/staffHome";

import ecoLogoWhite from "../../../img/eco/logo-white.png";
import ecoSimbolo from "../../../img/eco/simbolo.png";
import EcoIcon from "../../../components/eco-icon/EcoIcon";

import "./UnifiedLogin.css";

/**
 * Tela única de login — os três mundos de autenticação numa porta só.
 *
 * A pergunta "quem é você?" ficou DENTRO da tela: o seletor troca o formulário e
 * o endpoint, e nada é adivinhado. Tentar os três endpoints em sequência seria
 * um oráculo de enumeração de CPF, e não resolveria o caso real de um CPF que
 * existe em dois mundos ao mesmo tempo (staff e colaborador CLT). Ver ADR 0010.
 *
 * Continuam existindo três endpoints, três slices e três mundos de token: o que
 * mudou foi a quantidade de telas, de quatro para uma.
 */

type Perfil = "equipe" | "colaborador" | "freelancer";

const PERFIS: { id: Perfil; label: string; hint: string }[] = [
  { id: "equipe", label: "Equipe", hint: "Admin, supervisão e departamento pessoal" },
  { id: "colaborador", label: "Colaborador CLT", hint: "Registrado pela empresa; acesso ao ponto" },
  { id: "freelancer", label: "Freelancer", hint: "Diarista; entra só com o CPF" },
];

/** Chave de CONVENIÊNCIA por aparelho — não é sessão. A sessão vive no Redux. */
const PERFIL_STORAGE_KEY = "login:perfil";

const ehPerfil = (valor: unknown): valor is Perfil => PERFIS.some((p) => p.id === valor);

const lerPerfilSalvo = (): Perfil | null => {
  try {
    const salvo = window.localStorage.getItem(PERFIL_STORAGE_KEY);
    return ehPerfil(salvo) ? salvo : null;
  } catch {
    // Navegador com storage bloqueado: sem pré-seleção, a tela funciona igual.
    return null;
  }
};

const salvarPerfil = (perfil: Perfil) => {
  try {
    window.localStorage.setItem(PERFIL_STORAGE_KEY, perfil);
  } catch {
    /* conveniência; se não der para gravar, não é erro de login */
  }
};

const onlyDigits = (value: string) => String(value ?? "").replace(/\D/g, "");

/** Máscara progressiva de CPF — a mesma regra das telas do DP. */
const maskCpf = (value: string) =>
  onlyDigits(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})$/, "$1.$2.$3-$4");

const UnifiedLoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Ordem da pré-seleção: `?perfil=` (vem dos redirects das rotas antigas) →
  // escolha lembrada no aparelho → nenhuma. `?perfil=abc` é simplesmente
  // ignorado: uma URL estranha nunca deve quebrar a tela de entrada.
  const [perfil, setPerfil] = useState<Perfil | null>(() => {
    const daUrl = searchParams.get("perfil");
    if (ehPerfil(daUrl)) return daUrl;
    return lerPerfilSalvo();
  });

  const [identifier, setIdentifier] = useState("");
  const [cpf, setCpf] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Primeiro acesso do staff: a sessão fica RETIDA aqui até a senha ser trocada.
  // Enquanto `setCredentials` não for despachado não há sessão nenhuma, então
  // recarregar a página volta para cá — a troca deixou de ser pulável.
  const [primeiroAcesso, setPrimeiroAcesso] = useState<LoginResponse | null>(null);
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");

  const trocarPerfil = (novo: Perfil) => {
    if (novo === perfil) return;
    setPerfil(novo);
    setErrorMsg(null);
    setIdentifier("");
    setCpf("");
    setPassword("");
    setShowPassword(false);
  };

  const podeEnviar = useMemo(() => {
    if (loading) return false;

    if (perfil === "equipe") return identifier.trim().length > 0 && password.length > 0;
    if (perfil === "colaborador") return onlyDigits(cpf).length === 11 && password.length > 0;
    if (perfil === "freelancer") return onlyDigits(cpf).length === 11;

    return false;
  }, [perfil, identifier, cpf, password, loading]);

  /** Destino do staff pelo papel principal (DP e Financeiro têm home própria). */
  const destinoDoStaff = (accessToken: string) => staffHomeFromToken(accessToken);

  const entrarComoEquipe = async () => {
    const data = await loginApi({ identifier: identifier.trim(), password });

    if (data.isFirstLogin) {
      setPrimeiroAcesso(data);
      setPassword("");
      toast.info("Primeiro acesso: defina uma nova senha para continuar.");
      return;
    }

    salvarPerfil("equipe");
    dispatch(setCredentials(data));
    toast.success("Login realizado com sucesso. Bem-vindo ao Processos Zé!");
    navigate(destinoDoStaff(data.accessToken), { replace: true });
  };

  const entrarComoColaborador = async () => {
    const data = await loginEmployeeApi(onlyDigits(cpf), password);

    salvarPerfil("colaborador");
    dispatch(
      setEmployeeSession({
        accessToken: data.accessToken,
        tokenType: data.tokenType,
        expiresIn: data.expiresIn,
        isFirstLogin: data.isFirstLogin,
        fullName: data.employee.fullName,
      })
    );

    // Com a senha provisória em mãos, a única tela liberada é a de definir a
    // senha; o `EmployeeRoute` reforça isso mesmo se alguém digitar a URL.
    navigate(data.isFirstLogin ? COLABORADOR_PRIMEIRO_ACESSO() : COLABORADOR_HOME(), {
      replace: true,
    });
  };

  const entrarComoFreelancer = async () => {
    // Envia com máscara mesmo; o backend normaliza.
    const data = await loginFreelancerApi({ cpf });

    salvarPerfil("freelancer");
    dispatch(
      setFreelancerSession({
        accessToken: data.accessToken,
        cpf: data.freelancer.cpf,
        nomeCompleto: data.freelancer.nomeCompleto,
      })
    );

    toast.success(`Bem-vindo(a), ${data.freelancer.nomeCompleto}!`);
    navigate(FREELANCER_HOME_PAGE(), { replace: true });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!perfil || !podeEnviar) return;

    setErrorMsg(null);
    setLoading(true);

    try {
      if (perfil === "equipe") await entrarComoEquipe();
      else if (perfil === "colaborador") await entrarComoColaborador();
      else await entrarComoFreelancer();
    } catch (error: any) {
      // A mensagem é a que o backend devolveu, sem tratamento por perfil: no
      // colaborador ela é sempre "Credenciais inválidas." de propósito, e
      // acrescentar aqui um "esse CPF é de outro perfil" reconstruiria na tela o
      // oráculo de enumeração que o backend evita.
      const mensagem = error?.message || "Não foi possível fazer login.";
      setErrorMsg(mensagem);
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  const senhasConferem = confirmacao.length > 0 && novaSenha === confirmacao;
  const podeDefinirSenha = isStrongPassword(novaSenha) && senhasConferem && !loading;

  const definirSenhaDoStaff = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!primeiroAcesso || !podeDefinirSenha) return;

    setErrorMsg(null);
    setLoading(true);

    try {
      await changeFirstLoginPasswordApi(primeiroAcesso.accessToken, {
        password: novaSenha,
        passwordConfirmation: confirmacao,
      });

      // O token do staff continua valendo depois da troca (o backend não carimba
      // `passwordChangedAt` neste fluxo), então a sessão pode ser aberta aqui
      // mesmo — diferente do colaborador, que precisa entrar de novo.
      salvarPerfil("equipe");
      dispatch(setCredentials(primeiroAcesso));
      toast.success("Senha definida com sucesso. Bem-vindo!");
      navigate(destinoDoStaff(primeiroAcesso.accessToken), { replace: true });
    } catch (error: any) {
      const mensagem = error?.message || "Não foi possível definir a senha.";
      setErrorMsg(mensagem);
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };


  const campoSenha = (id: string, rotulo: string) => (
    <div className="authField">
      <label htmlFor={id}>{rotulo}</label>
      <div className="authPassword">
        <input
          id={id}
          className="authInput authInput--withAction"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          placeholder="Sua senha"
          disabled={loading}
        />
        <button
          type="button"
          className="authPassword__toggle"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          title={showPassword ? "Ocultar senha" : "Mostrar senha"}
          disabled={loading}
        >
          <EcoIcon name={showPassword ? "eyeOff" : "eye"} strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );

  const campoCpf = (id: string) => (
    <div className="authField">
      <label htmlFor={id}>CPF</label>
      <input
        id={id}
        className="authInput"
        value={cpf}
        onChange={(e) => setCpf(maskCpf(e.target.value))}
        // `inputMode` numérico abre o teclado de números no celular sem impedir
        // a máscara com pontos e traço.
        inputMode="numeric"
        autoComplete="username"
        placeholder="000.000.000-00"
        disabled={loading}
      />
    </div>
  );

  const erro = errorMsg ? (
    <p className="authError" role="alert">
      {errorMsg}
    </p>
  ) : null;

  // ---- Primeiro acesso do staff: ocupa a tela toda, sem saída ----------------
  if (primeiroAcesso) {
    return (
      <AuthShell
        mobileTitle="Crie a sua senha"
        mobileSubtitle="Primeiro acesso ao Processos Zé."
      >
        <form className="authPanel" onSubmit={definirSenhaDoStaff}>
          <div className="authHead">
            <img className="authHead__symbol" src={ecoSimbolo} alt="" aria-hidden="true" />
            <h2 className="authTitle">Crie a sua senha</h2>
            <p className="authSubtitle">
              A senha atual é provisória. Escolha uma senha só sua para continuar.
            </p>
          </div>

          {erro}

          <div className="authFields">
            <div className="authField">
              <label htmlFor="staff-nova-senha">Nova senha</label>
              <input
                id="staff-nova-senha"
                className="authInput"
                type="password"
                autoComplete="new-password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                disabled={loading}
              />
            </div>

            <ul className="authRules">
              {PASSWORD_RULES.map((regra) => (
                <li key={regra.id} className={regra.test(novaSenha) ? "is-ok" : ""}>
                  {regra.label}
                </li>
              ))}
            </ul>

            <div className="authField">
              <label htmlFor="staff-confirma-senha">Repita a nova senha</label>
              <input
                id="staff-confirma-senha"
                className="authInput"
                type="password"
                autoComplete="new-password"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                disabled={loading}
              />
              {confirmacao.length > 0 && !senhasConferem ? (
                <span className="authHint authHint--error">As senhas não são iguais.</span>
              ) : null}
            </div>
          </div>

          <div className="authActions">
            <button type="submit" className="authButton" disabled={!podeDefinirSenha}>
              {loading ? "Salvando..." : "Salvar e entrar"}
              {!loading ? <EcoIcon name="arrowRight" size={18} strokeWidth={2.2} /> : null}
            </button>
            <p className="authHint">A troca é obrigatória: sem ela não há acesso ao sistema.</p>
          </div>
        </form>
      </AuthShell>
    );
  }

  const perfilAtual = PERFIS.find((p) => p.id === perfil);

  // ---- Tela normal -----------------------------------------------------------
  return (
    <AuthShell
      mobileTitle="Bem-vindo de volta"
      mobileSubtitle="Escolha como você acessa o Processos Zé."
    >
      <div className="authPanel">
        <div className="authHead">
          <img className="authHead__symbol" src={ecoSimbolo} alt="" aria-hidden="true" />
          <h2 className="authTitle">Bem-vindo de volta</h2>
          <p className="authSubtitle">
            Escolha como você acessa o sistema e entre com os seus dados.
          </p>
        </div>

        <div className="authProfile">
          <span className="authProfile__legend" id="auth-perfil-legend">
            Perfil de acesso
          </span>
          <div className="authSegmented" role="group" aria-labelledby="auth-perfil-legend">
            {PERFIS.map((opcao) => (
              <button
                key={opcao.id}
                type="button"
                className={`authSegmented__option${perfil === opcao.id ? " is-selected" : ""}`}
                aria-pressed={perfil === opcao.id}
                onClick={() => trocarPerfil(opcao.id)}
                disabled={loading}
              >
                {opcao.label}
              </button>
            ))}
          </div>
          <p className="authProfile__hint">
            {perfilAtual ? `${perfilAtual.hint}.` : "Escolha o seu perfil para continuar."}
          </p>
        </div>

        {perfil ? (
          <form className="authForm" onSubmit={handleSubmit}>
            <div className="authFields">
              {perfil === "equipe" ? (
                <>
                  <div className="authField">
                    <label htmlFor="equipe-identifier">E-mail ou CPF</label>
                    <input
                      id="equipe-identifier"
                      className="authInput"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      // Sem máscara: este campo aceita e-mail OU CPF, e é o backend
                      // que decide qual dos dois veio.
                      placeholder="nome@processosze.com.br"
                      autoComplete="username"
                      disabled={loading}
                    />
                  </div>
                  {campoSenha("equipe-senha", "Senha")}
                </>
              ) : null}

              {perfil === "colaborador" ? (
                <>
                  {campoCpf("colaborador-cpf")}
                  {campoSenha("colaborador-senha", "Senha")}
                </>
              ) : null}

              {perfil === "freelancer" ? campoCpf("freelancer-cpf") : null}
            </div>

            {erro}

            <div className="authActions">
              <button type="submit" className="authButton" disabled={!podeEnviar}>
                {loading ? "Entrando..." : "Entrar"}
                {!loading ? <EcoIcon name="arrowRight" size={18} strokeWidth={2.2} /> : null}
              </button>

              {perfil === "colaborador" ? (
                <p className="authHint">
                  <strong>Esqueceu a senha?</strong> Procure o Departamento Pessoal: a senha é
                  reemitida pessoalmente, no balcão. O sistema não envia senha por e-mail nem por
                  mensagem.
                </p>
              ) : null}

              {perfil === "freelancer" ? (
                <p className="authHint">
                  Não tem conta?{" "}
                  <button
                    type="button"
                    className="authLink"
                    onClick={() => navigate(FREELANCER_REGISTER_PAGE())}
                  >
                    Cadastre-se
                  </button>
                </p>
              ) : null}
            </div>
          </form>
        ) : null}
      </div>
    </AuthShell>
  );
};

/**
 * Moldura das duas telas (login e primeiro acesso): painel da marca + coluna do
 * formulário. No desktop o painel ocupa a metade esquerda com o texto
 * institucional; no celular vira uma faixa no topo com título próprio.
 */
const AuthShell: React.FC<{
  mobileTitle: string;
  mobileSubtitle: string;
  children: React.ReactNode;
}> = ({ mobileTitle, mobileSubtitle, children }) => (
  <div className="authPage">
    <section className="authHero">
      <div className="authHero__top">
        <img className="authHero__logo" src={ecoLogoWhite} alt="Eco System Serviços" />
        <span className="authHero__tag">Processos Zé</span>
      </div>

      <div className="authHero__pitch">
        <h1 className="authHero__title">Operações, pessoas e estoque num só lugar.</h1>
        <p className="authHero__text">
          Escalas, avaliações de loja, departamento pessoal, FEFO e balanço de massa, com a
          mesma porta de entrada para toda a equipe.
        </p>
        <ul className="authHero__chips">
          <li>Escalas e plantões</li>
          <li>Departamento Pessoal</li>
          <li>Estoque e FEFO</li>
        </ul>
      </div>

      <div className="authHero__welcome">
        <p className="authHero__welcomeTitle">{mobileTitle}</p>
        <p className="authHero__welcomeText">{mobileSubtitle}</p>
      </div>

      <p className="authHero__copy">© Eco System Serviços</p>
    </section>

    <section className="authMain">{children}</section>

    <p className="authPage__copy">© Eco System Serviços</p>
  </div>
);

export default UnifiedLoginPage;
